import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { savedFiltersApi } from './api';

/**
 * Before T27 the admin Bills page kept its saved views in this browser only,
 * as a zustand-persisted list of query strings (T20 stored preset ranges as
 * `period=…`). On first load they move to the server as private favourites
 * and the browser copy is removed.
 */
export const LEGACY_BILL_VIEWS_KEY = 'bbpos-admin-bill-list-views:v1';

export interface LegacyBillView {
  name: string;
  filters: Record<string, string>;
}

/** The stored views as saved filters; anything unreadable is skipped. */
export function legacyBillViews(raw: string | null): LegacyBillView[] {
  if (!raw) return [];
  let views: unknown;
  try {
    views = (JSON.parse(raw) as { state?: { views?: unknown } })?.state?.views;
  } catch {
    return [];
  }
  if (!Array.isArray(views)) return [];
  const out: LegacyBillView[] = [];
  for (const v of views as Array<{ name?: unknown; query?: unknown }>) {
    if (typeof v?.name !== 'string' || typeof v.query !== 'string') continue;
    const name = v.name.trim().slice(0, 80);
    if (!name) continue;
    const filters: Record<string, string> = {};
    for (const [key, value] of new URLSearchParams(v.query.replace(/^\?/, ''))) {
      if (value && key !== 'page' && key !== 'limit') filters[key] = value;
    }
    out.push({ name, filters });
  }
  return out;
}

function readKey(): string | null {
  try {
    return localStorage.getItem(LEGACY_BILL_VIEWS_KEY);
  } catch {
    return null;
  }
}

let migrating = false;

/** Moves this browser's old Bills views to the server once; kept for the next load if a save fails. */
export function useMigrateLegacyBillViews(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (migrating) return;
    const views = legacyBillViews(readKey());
    if (views.length === 0) return;
    migrating = true;
    (async () => {
      try {
        // Oldest first, so a later view with the same name wins, as it did in the browser.
        for (const view of [...views].reverse()) {
          await savedFiltersApi.create({ page: 'bills', name: view.name, filters: view.filters });
        }
        try {
          localStorage.removeItem(LEGACY_BILL_VIEWS_KEY);
        } catch {
          /* storage blocked — the views are on the server now anyway */
        }
        await queryClient.invalidateQueries({ queryKey: ['saved-filters'] });
      } catch {
        /* offline or refused — try again on the next load */
      } finally {
        migrating = false;
      }
    })();
  }, []);
}
